import { ApplicationConfig, ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling, withNavigationErrorHandler, withRouterConfig, withViewTransitions } from '@angular/router';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeuix/themes/aura';
import { definePreset } from '@primeuix/themes';
import { provideStore } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideEchartsCore } from 'ngx-echarts';
import { TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as echarts from 'echarts/core';
import { BarChart, LineChart, PieChart } from 'echarts/charts';
import {
  GridComponent, TooltipComponent, LegendComponent,
  DataZoomComponent, ToolboxComponent, MarkLineComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  BarChart, LineChart, PieChart,
  GridComponent, TooltipComponent, LegendComponent,
  DataZoomComponent, ToolboxComponent, MarkLineComponent,
  CanvasRenderer,
]);

import { environment } from '../environments/environment';
import { routes } from './app.routes';
import {
  authInterceptor,
  errorInterceptor,
  loadingInterceptor
} from './core/interceptors';
import { claimsReducer } from './features/claims/store/claims.reducer';
import { ClaimsEffects } from './features/claims/store/claims.effects';
import { CLAIMS_FEATURE_KEY } from './features/claims/store/claims.selectors';
import { healthTravelReducer } from './features/health-travel/store/health-travel.reducer';
import { HealthTravelEffects } from './features/health-travel/store/health-travel.effects';
import { HEALTH_TRAVEL_FEATURE_KEY } from './features/health-travel/store/health-travel.selectors';
import { profileReducer } from './core/store/profile/profile.reducer';
import { ProfileEffects } from './core/store/profile/profile.effects';
import { PROFILE_FEATURE_KEY } from './core/store/profile/profile.selectors';
import { personReducer } from './core/store/person/person.reducer';
import { PersonEffects } from './core/store/person/person.effects';
import { PERSON_FEATURE_KEY } from './core/store/person/person.selectors';
import { policyAnalyticsReducer } from './core/store/policy-analytics/policy-analytics.reducer';
import { PolicyAnalyticsEffects } from './core/store/policy-analytics/policy-analytics.effects';
import { POLICY_ANALYTICS_FEATURE_KEY } from './core/store/policy-analytics/policy-analytics.selectors';
import { adminReducer } from './features/admin/store/admin.reducer';
import { AdminEffects } from './features/admin/store/admin.effects';
import { ADMIN_FEATURE_KEY } from './features/admin/store/admin.selectors';
import { warehouseReducer } from './features/warehouse/store/warehouse.reducer';
import { WarehouseEffects } from './features/warehouse/store/warehouse.effects';
import { WAREHOUSE_FEATURE_KEY } from './features/warehouse/store/warehouse.selectors';
import { claimAnalyticsReducer } from './core/store/claim-analytics/claim-analytics.reducer';
import { ClaimAnalyticsEffects } from './core/store/claim-analytics/claim-analytics.effects';
import { CLAIM_ANALYTICS_FEATURE_KEY } from './core/store/claim-analytics/claim-analytics.selectors';
import { offersPoliciesReducer } from './features/offers-policies/store/offers-policies.reducer';
import { OffersPoliciesEffects } from './features/offers-policies/store/offers-policies.effects';
import { OFFERS_POLICIES_FEATURE_KEY } from './features/offers-policies/store/offers-policies.selectors';

/**
 * Recovers from stale-chunk errors after a Vercel deploy.
 *
 * When we ship a new build, Vercel replaces every lazy-loaded chunk with a
 * new content-hash filename. A tab that's been open since before the deploy
 * is still holding references to the OLD chunk names in its route config.
 * The moment the user clicks a link that triggers a lazy import, the browser
 * requests `chunk-ABCDEFGH.js` → 404 → the dynamic import rejects with
 * `TypeError: Failed to fetch dynamically imported module`.
 *
 * Detect that specific error and do a one-shot page reload so the tab picks
 * up the new build. A sessionStorage timestamp prevents reload loops when
 * the reload itself fails (e.g. offline, server error on index.html).
 */
const CHUNK_RELOAD_KEY = 'ip:lastChunkReload';
function isChunkLoadError(err: unknown): boolean {
  const msg = String((err as { message?: unknown })?.message ?? err ?? '');
  return /dynamically imported module|ChunkLoadError|Loading chunk .* failed|error loading dynamically imported module|importing a module script failed/i.test(msg);
}
function recoverFromChunkLoadError(err: unknown): void {
  if (!isChunkLoadError(err)) return;
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  const now = Date.now();
  // Skip if we reloaded in the last 10s — prevents hot loops.
  if (now - last < 10_000) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(now));
  window.location.reload();
}

class ChunkAwareErrorHandler implements ErrorHandler {
  handleError(err: unknown): void {
    recoverFromChunkLoadError(err);
    // Log so the original stack still shows in devtools even if we reload.
    console.error(err);
  }
}

// Custom brand preset with your blue colors
const InsurancePortalPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#eaf3ff',
      100: '#d6e9ff',
      200: '#add4ff',
      300: '#7fbaff',
      400: '#4796ff',
      500: '#1366d6',
      600: '#0d4ea8',
      700: '#0c3b80',
      800: '#0b2f66',
      900: '#0a2450',
      950: '#071a3d'
    },
    colorScheme: {
      light: {
        primary: {
          color: '#1366d6',
          hoverColor: '#0d4ea8',
          activeColor: '#0c3b80'
        },
        highlight: {
          background: '#eaf3ff',
          focusBackground: '#d6e9ff',
          color: '#0c3b80',
          focusColor: '#0a2450'
        }
      },
      dark: {
        primary: {
          color: '#7fbaff',
          hoverColor: '#add4ff',
          activeColor: '#d6e9ff'
        },
        highlight: {
          background: 'rgba(127, 186, 255, 0.16)',
          focusBackground: 'rgba(127, 186, 255, 0.24)',
          color: 'rgba(255,255,255,.87)',
          focusColor: 'rgba(255,255,255,.87)'
        }
      }
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),

    provideRouter(
      routes,
      withComponentInputBinding(),
      // A navigation the leave guard cancels (browser Back → "Qëndro") must keep
      // the history entry the browser already moved to; the default 'replace'
      // overwrites it, so the next Back skips a page.
      withRouterConfig({ canceledNavigationResolution: 'computed' }),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled'
      }),
      withViewTransitions(),
      // Auto-reload on stale-chunk errors so users don't get stuck on a
      // dead link after we deploy a new build to Vercel. See the comment
      // on recoverFromChunkLoadError above.
      withNavigationErrorHandler(recoverFromChunkLoadError),
    ),

    // Belt-and-suspenders: catch chunk-load errors that fire OUTSIDE of
    // router navigation (lazy modals, dynamic imports from a component).
    { provide: ErrorHandler, useClass: ChunkAwareErrorHandler },

    provideAnimationsAsync(),

    providePrimeNG({
      theme: {
        preset: InsurancePortalPreset,
        options: {
          prefix: 'p',
          darkModeSelector: '.dark-mode',
          cssLayer: false
        }
      },
      ripple: true
    }),

    provideHttpClient(
      withFetch(),
      withInterceptors([
        authInterceptor,
        loadingInterceptor,
        errorInterceptor
      ])
    ),

    provideStore({
      [CLAIMS_FEATURE_KEY]:        claimsReducer,
      [HEALTH_TRAVEL_FEATURE_KEY]: healthTravelReducer,
      [PROFILE_FEATURE_KEY]:       profileReducer,
      [PERSON_FEATURE_KEY]:                personReducer,
      [POLICY_ANALYTICS_FEATURE_KEY]:      policyAnalyticsReducer,
      [ADMIN_FEATURE_KEY]:                 adminReducer,
      [WAREHOUSE_FEATURE_KEY]:             warehouseReducer,
      [CLAIM_ANALYTICS_FEATURE_KEY]:       claimAnalyticsReducer,
      [OFFERS_POLICIES_FEATURE_KEY]:       offersPoliciesReducer,
    }),
    provideEffects([ClaimsEffects, HealthTravelEffects, ProfileEffects, PersonEffects, PolicyAnalyticsEffects, AdminEffects, WarehouseEffects, ClaimAnalyticsEffects, OffersPoliciesEffects]),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),

    provideEchartsCore({ echarts }),

    TranslateModule.forRoot({ fallbackLang: 'sq' }).providers!,
    ...provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json', useHttpBackend: true }),
  ]
};